import { Link, useLocation } from "react-router-dom";
import { CaretRight } from "@phosphor-icons/react";
import { WORKSPACE_REGISTRY } from "@/nav/workspaceRegistry";

/**
 * Breadcrumbs (Sprint 6 · LAW-007).
 *
 * Derives its trail exclusively from `WORKSPACE_REGISTRY`. Walks the
 * current pathname → finds the owning workspace + component → renders
 *
 *   Workspace ›  Component
 *
 * Nothing about breadcrumbs is hand-authored per route. Adding, moving,
 * or renaming a workspace/component in the registry is enough.
 *
 * On sub-routes (e.g. `/receiving/abc123`) we fall back to the closest
 * matching prefix (`/receiving`) so detail pages inherit their parent's
 * breadcrumb without extra config.
 */

// Flatten the registry once at module load. Keeps lookups O(n) on a
// small list (5 workspaces * ~5 components).
const FLAT_COMPONENTS = WORKSPACE_REGISTRY.flatMap((ws) =>
  ws.components.map((c) => ({ ...c, workspace: ws }))
);

// Strip query string / hash for path matching.
function normalisePath(pathname) {
  return pathname.split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";
}

// Best match: exact path first, then longest prefix.
function findRegistryMatch(pathname) {
  const p = normalisePath(pathname);
  const targetPath = (to) => normalisePath(to.split("?")[0]);

  const exact = FLAT_COMPONENTS.find((c) => targetPath(c.to) === p);
  if (exact) return exact;

  const prefixMatches = FLAT_COMPONENTS
    .filter((c) => {
      const t = targetPath(c.to);
      return t !== "/" && (p === t || p.startsWith(t + "/"));
    })
    .sort((a, b) => targetPath(b.to).length - targetPath(a.to).length);

  return prefixMatches[0] || null;
}

export default function Breadcrumbs() {
  const { pathname } = useLocation();
  const match = findRegistryMatch(pathname);

  if (!match) {
    return null;
  }

  const { workspace, label, to } = match;
  const onLeaf = normalisePath(pathname) === normalisePath(to.split("?")[0]);

  return (
    <nav
      data-testid="breadcrumbs"
      aria-label="Breadcrumb"
      className="flex items-center gap-1.5 border-b border-divider bg-surface px-5 py-2 text-[13px] text-ink-muted sm:px-8 lg:px-10"
    >
      <span
        data-testid="breadcrumb-workspace"
        className="font-semibold uppercase tracking-wider text-[11px] text-ink-muted"
      >
        {workspace.label}
      </span>
      <CaretRight size={12} weight="bold" className="text-ink-muted" />
      {onLeaf ? (
        <span
          data-testid="breadcrumb-component"
          aria-current="page"
          className="font-medium text-charcoal"
        >
          {label}
        </span>
      ) : (
        <>
          <Link
            data-testid="breadcrumb-component"
            to={to}
            className="font-medium text-brand hover:text-brand-hover hover:underline"
          >
            {label}
          </Link>
          <CaretRight size={12} weight="bold" className="text-ink-muted" />
          <span
            data-testid="breadcrumb-leaf"
            aria-current="page"
            className="font-medium text-charcoal"
          >
            Detail
          </span>
        </>
      )}
    </nav>
  );
}
